import { Controller } from '@hotwired/stimulus'

interface MismatchEntry {
  id: number
  section: string
}

const HIGHLIGHT_CLASSES = [
  'tw-ring-1',
  'tw-ring-yellow-300',
  'tw-ring-offset-2',
  'tw-relative',
  'tw-z-10',
] as const

const DISABLED_BUTTON_CLASSES = [
  'tw-opacity-50',
  'tw-cursor-not-allowed',
] as const

const ENABLED_BUTTON_CLASSES = ['tw-cursor-pointer'] as const

export default class extends Controller {
  static targets = ['counter', 'nextButton', 'previousButton']
  static values = {
    total: Number,
    mismatchIndex: Array,
  }

  declare readonly counterTarget: HTMLElement
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly previousButtonTarget: HTMLButtonElement
  declare totalValue: number
  declare mismatchIndexValue: MismatchEntry[]

  private currentIndex: number = -1
  private readyRows: Set<number> = new Set()
  private pendingNavigation: boolean = false
  private currentlyHighlightedRow: HTMLElement | null = null

  connect(): void {
    this.updateButtonStates()

    document.addEventListener(
      'mismatch-row:ready',
      this.handleRowReady.bind(this) as EventListener
    )
    document.addEventListener(
      'mismatch:finalized',
      this.handleMismatchFinalized.bind(this) as EventListener
    )
  }

  disconnect(): void {
    document.removeEventListener(
      'mismatch-row:ready',
      this.handleRowReady.bind(this) as EventListener
    )
    document.removeEventListener(
      'mismatch:finalized',
      this.handleMismatchFinalized.bind(this) as EventListener
    )
  }

  private handleRowReady(event: CustomEvent<{ id: number }>): void {
    const accountLinkId = event.detail.id
    this.readyRows.add(accountLinkId)

    if (this.pendingNavigation && this.currentIndex >= 0) {
      const currentEntry = this.mismatchIndexValue[this.currentIndex]
      if (currentEntry && currentEntry.id === accountLinkId) {
        this.scrollToCurrentMismatch()
        this.pendingNavigation = false
      }
    }
  }

  next(): void {
    if (this.currentIndex >= this.mismatchIndexValue.length - 1) {
      return
    }

    if (this.currentlyHighlightedRow !== null || this.currentIndex === -1) {
      this.currentIndex++
    }
    this.navigateToMismatch()
  }

  previous(): void {
    if (this.currentIndex <= 0) return

    this.currentIndex--
    this.navigateToMismatch()
  }

  private navigateToMismatch(): void {
    const entry = this.mismatchIndexValue[this.currentIndex]

    // Check if section is expanded
    const section = document.getElementById(entry.section)
    if (!section || !section.hasAttribute('data-section-expanded')) {
      // Need to expand the section first
      this.expandSection(entry.section)
      this.pendingNavigation = true
    } else if (this.readyRows.has(entry.id)) {
      // Row is already in DOM
      this.scrollToCurrentMismatch()
    } else {
      // Section is expanded but row not ready yet, wait for it
      this.pendingNavigation = true
    }

    this.updateButtonStates()
  }

  private handleMismatchFinalized(event: CustomEvent<{ id: number }>): void {
    this.handleMarkAsCorrect(event.detail.id)
  }

  private handleMarkAsCorrect(accountLinkId: number): void {
    const index = this.mismatchIndexValue.findIndex(
      (e) => e.id === accountLinkId
    )
    if (index === -1) return

    // Create new array to trigger Stimulus value change (splice doesn't trigger reactivity)
    this.mismatchIndexValue = [
      ...this.mismatchIndexValue.slice(0, index),
      ...this.mismatchIndexValue.slice(index + 1),
    ]
    this.readyRows.delete(accountLinkId)

    const newTotal = this.totalValue - 1
    if (newTotal === 0) {
      setTimeout(() => {
        this.element.remove()
      }, 100)
      return
    }

    this.totalValue = newTotal
    this.counterTarget.textContent =
      this.counterTarget.textContent?.replace(/\d+/, newTotal.toString()) || ''

    const isCurrentRow = index === this.currentIndex

    if (index < this.currentIndex) {
      // Item removed before current position, shift index back
      this.currentIndex--
    } else if (index === this.currentIndex) {
      // Current item removed - stay at same index (now points to next item)
      // But if we're now beyond the array, move back
      if (
        this.currentIndex >= this.mismatchIndexValue.length &&
        this.currentIndex > 0
      ) {
        this.currentIndex--
      }
    }

    if (isCurrentRow && this.currentlyHighlightedRow) {
      this.currentlyHighlightedRow.classList.remove(...HIGHLIGHT_CLASSES)
      this.currentlyHighlightedRow = null
    }

    this.updateButtonStates()
  }

  private expandSection(sectionId: string): void {
    const section = document.getElementById(sectionId)
    if (!section) return

    const link = section.querySelector('a[data-turbo-stream]') as HTMLElement
    if (!link) return

    const chevron = section.querySelector('.fa-chevron-right') as HTMLElement
    if (chevron) {
      chevron.classList.remove('fa-chevron-right', 'sf-pad-r-2')
      chevron.classList.add('fa-spinner', 'fa-spin')
      chevron.style.marginRight = '0.5rem'
    }

    link.click()
  }

  private scrollToCurrentMismatch(): void {
    if (
      this.currentIndex < 0 ||
      this.currentIndex >= this.mismatchIndexValue.length
    ) {
      return
    }

    const entry = this.mismatchIndexValue[this.currentIndex]
    const row = document.getElementById(`account_link_${entry.id}`)

    if (!row) {
      return
    }

    row.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })

    this.highlightRow(row)
    this.updateButtonStates()
  }

  private highlightRow(row: HTMLElement): void {
    if (this.currentlyHighlightedRow) {
      this.currentlyHighlightedRow.classList.remove(...HIGHLIGHT_CLASSES)
    }

    row.classList.add(...HIGHLIGHT_CLASSES)

    this.currentlyHighlightedRow = row
  }

  private updateButtonStates(): void {
    this.previousButtonTarget.disabled = this.currentIndex <= 0
    this.nextButtonTarget.disabled =
      this.totalValue === 0 ||
      this.currentIndex >= this.mismatchIndexValue.length - 1

    this.updateButtonStyle(this.previousButtonTarget)
    this.updateButtonStyle(this.nextButtonTarget)
  }

  private updateButtonStyle(button: HTMLButtonElement): void {
    if (button.disabled) {
      button.classList.add(...DISABLED_BUTTON_CLASSES)
      button.classList.remove(...ENABLED_BUTTON_CLASSES)
    } else {
      button.classList.remove(...DISABLED_BUTTON_CLASSES)
      button.classList.add(...ENABLED_BUTTON_CLASSES)
    }
  }
}
